/**
 * AI CONTEXT
 * Purpose: central attendance board for selected sessions in front-desk/admin workspace.
 * Composes: session summary cards + per-session student list container.
 * Inputs: AdminViewContext selectedSessions/students/exception data.
 * Used by: SuperView and legacy AdminView.
 * Refactor: avoid in-place sorting of context arrays (copy before sort).
 */
import { useMemo } from "react"

import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import Flex from "#blocks/Flex"
import List from "#blocks/List"
import { HEADER_HEIGHT } from "#components/Header"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { AdminViewClassSession } from "#hooks/newHooks/useAdminViewData"
import usePreventWindowFileDrop from "#hooks/newHooks/usePreventWindowFileDrop"
import AttendanceViewSessionSummary from "#views/AdminView/AttendanceView/AttendanceViewSessionSummary"

import AttendanceViewStudentListContainer from "./AttendanceViewStudentListContainer"

const AttendanceViewItemContent = ({
  item,
}: {
  item: AdminViewClassSession
}) => {
  const { studentsByExceptionType, studentsById } = useAdminViewContext()
  const { classGroup } = item

  return (
    <Flex
      key={item.id}
      col
      gap={10}
      style={{ position: "relative", marginBottom: 20 }}
    >
      <Flex
        style={{
          position: "relative",
          top: 0,
          left: 0,
          zIndex: 10,
        }}
      >
        <AttendanceViewSessionSummary adminViewSession={item} />
      </Flex>
      <AttendanceViewStudentListContainer
        notes={item.notes}
        session={item.session}
        classGroup={classGroup}
        studentsByExceptionType={studentsByExceptionType}
        studentsById={studentsById}
      />
    </Flex>
  )
}

const AttendanceViewItem = ({ item }: { item: AdminViewClassSession }) => (
  <ComponentErrorBoundary
    context={`Class: ${item.name}`}
    fallbackStyle={{ width: 350, minHeight: 120 }}
  >
    <AttendanceViewItemContent item={item} />
  </ComponentErrorBoundary>
)

const AttendanceView = () => {
  const { selectedSessions } = useAdminViewContext()
  // Rows are drop targets; a drop that misses one must not navigate the page.
  usePreventWindowFileDrop()
  const sortedSessions = useMemo(() => {
    return selectedSessions.sort((a, b) => {
      return a.startTime.diff(b.startTime)
    })
  }, [selectedSessions])

  return (
    <Flex
      col
      gap={20}
      style={{
        flexGrow: 1,
        height: `calc(100vh - ${HEADER_HEIGHT + 100}px)`,
        width: "fit-content",
        maxWidth: "calc(100vw - 615px - 40px)",
      }}
    >
      <List
        layout="grid"
        items={sortedSessions}
        component={AttendanceViewItem}
      />
    </Flex>
  )
}

export default AttendanceView
